/**
 * PATH: packages/ui/src/components/boxResize.ts
 * FILE_ID: UI-CMP-BOX-RESIZE-001
 * PURPOSE: Pure 8-way box resize math. Edges in the axis name move;
 *          the opposite edge is pinned. Same input, same output, no DOM.
 * WHY: FilesDrawer already did this inline. The ITP popup (and later
 *      any floating panel) must share one clamp so corner drags cannot
 *      strand a dialog off screen.
 */
export type BoxResizeAxis = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'

export interface Box {
  x: number
  y: number
  w: number
  h: number
}

export interface BoxBounds {
  minW: number
  minH: number
  maxW: number
  maxH: number
  /** Viewport inset the box may not cross. */
  margin: number
  vpW: number
  vpH: number
}

export const BOX_RESIZE_AXES: BoxResizeAxis[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw']

export const BOX_RESIZE_CURSORS: Record<BoxResizeAxis, string> = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  nw: 'nwse-resize',
  se: 'nwse-resize',
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

export function applyBoxResize(
  start: Box,
  axis: BoxResizeAxis,
  dx: number,
  dy: number,
  b: BoxBounds,
): Box {
  let { x, y, w, h } = start
  const maxRight = b.vpW - b.margin
  const maxBottom = b.vpH - b.margin

  if (axis.includes('e')) {
    w = clamp(start.w + dx, b.minW, Math.min(b.maxW, maxRight - start.x))
  }
  if (axis.includes('w')) {
    const maxDx = start.w - b.minW
    const minDx = b.margin - start.x
    const clampedDx = Math.min(Math.max(dx, minDx), maxDx)
    x = start.x + clampedDx
    w = start.w - clampedDx
  }
  if (axis.includes('s')) {
    h = clamp(start.h + dy, b.minH, Math.min(b.maxH, maxBottom - start.y))
  }
  if (axis.includes('n')) {
    const maxDy = start.h - b.minH
    const minDy = b.margin - start.y
    const clampedDy = Math.min(Math.max(dy, minDy), maxDy)
    y = start.y + clampedDy
    h = start.h - clampedDy
  }
  return { x, y, w, h }
}
